import { Link } from 'wouter';
import {
  describePlannedPerson,
  describeUnresolvedMembers,
  summariseDryRun,
  summariseMemberResolution,
  summariseRun,
} from '../affinity/format.js';
import type { Affinity } from '../affinity/useAffinity.js';
import { runPhrase } from '../dealroom/format.js';
import type { Dealroom } from '../dealroom/useDealroom.js';
import { isActive } from '../research/status.js';
import type { Research } from '../research/useResearch.js';
import { CollapsibleCard } from './CollapsibleCard.js';
import { PeopleChanges } from './PeopleChanges.js';
import { PossibleDuplicates } from './PossibleDuplicates.js';
import { ReportedContacts } from './ReportedContacts.js';
import { StageConflicts } from './StageConflicts.js';
import type { EnrichmentStatus } from './types.js';

export interface EnrichmentDrawerProps {
  status: EnrichmentStatus | undefined;
  research: Research;
  /** Which funds "Research" would act on, and the label the button reads. */
  researchTargets: { ids: string[]; label: string };
  /** The rows selected on the page; a people check over none reads the whole main list. */
  selectedFundIds: readonly string[];
  affinity: Affinity;
  dealroom: Dealroom;
  onClose: () => void;
}

/**
 * The enrichment sources, each in its own card: what the funds page's toolbar
 * used to spread across five buttons and two status lines, plus the reported
 * contacts nobody could place. Opening this replaces the fund panel in the
 * same slot, because a person is not meant to read both at once.
 */
export function EnrichmentDrawer(props: EnrichmentDrawerProps): React.JSX.Element {
  const { status } = props;
  const activeResearch = [...props.research.byFund.values()].filter(isActive).length;
  const researchStatus = activeResearch > 0 ? `Running · ${activeResearch} funds` : null;
  const lastRun = props.affinity.lastRun;
  const affinityStatus =
    lastRun?.status === 'running' || lastRun?.status === 'queued'
      ? `${lastRun.status === 'running' ? 'Running' : 'Queued'} · ${lastRun.callsMade} of ${lastRun.ceiling}`
      : null;
  const dealroomRunning = status?.dealroom.lastRunByKind.some(
    (run) => run.status === 'running' || run.status === 'queued',
  );
  const dealroomStatus =
    dealroomRunning === true && status !== undefined
      ? `Running · ${status.dealroom.callsToday} of ${status.dealroom.ceilingPerDay}`
      : null;

  return (
    <aside className="panel">
      <header className="panel-head">
        <div className="panel-title">
          <h2>Enrichment</h2>
        </div>
        <button className="button small" type="button" onClick={props.onClose}>
          Close
        </button>
      </header>

      <CollapsibleCard id="research" title="Research" status={researchStatus}>
        <button
          className="button small"
          type="button"
          disabled={props.research.isStarting || props.researchTargets.ids.length === 0}
          onClick={() => props.research.start(props.researchTargets.ids)}
        >
          Research {props.researchTargets.label}
        </button>
        {status !== undefined && (
          <p className="subtle">
            ${status.research.estimate.spentTodayUsd.toFixed(2)} of $
            {status.research.estimate.ceilingUsd.toFixed(2)} today’s ceiling used ·{' '}
            {pendingFundsPhrase(status.research.pendingFundsCount)}
          </p>
        )}
      </CollapsibleCard>

      <CollapsibleCard id="affinity" title="Affinity" status={affinityStatus}>
        <button
          className="button small"
          type="button"
          disabled={props.affinity.isStarting}
          onClick={props.affinity.start}
        >
          Sync Affinity
        </button>{' '}
        <button
          className="button small"
          type="button"
          disabled={props.affinity.isDryRunning}
          onClick={props.affinity.dryRun}
        >
          {props.affinity.isDryRunning ? 'Reading…' : 'Dry run'}
        </button>
        <p className="subtle">{summariseRun(props.affinity.lastRun)}</p>
        {props.affinity.plan !== null && (
          <details className="enrichment-plan" open>
            <summary className="subtle">{summariseDryRun(props.affinity.plan)}</summary>
            <p className="subtle">
              {summariseMemberResolution(props.affinity.plan.plan.memberResolution)}
            </p>
            {describeUnresolvedMembers(props.affinity.plan.plan.memberResolution).length > 0 && (
              <ul className="subtle">
                {describeUnresolvedMembers(props.affinity.plan.plan.memberResolution).map(
                  (line) => (
                    <li key={line}>{line}</li>
                  ),
                )}
              </ul>
            )}
            {props.affinity.plan.plan.funds.map((fund) => (
              <div key={fund.fundName}>
                <p className="subtle">
                  {fund.fundName} —{' '}
                  {fund.organizationName ?? fund.organizationId ?? 'no organisation'}, {fund.calls}{' '}
                  calls
                </p>
                <ul className="subtle">
                  {fund.people.map((person) => (
                    <li key={`${person.affinityPersonId ?? ''}:${person.investorName ?? ''}`}>
                      {describePlannedPerson(person)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <button className="button small" type="button" onClick={props.affinity.dismissPlan}>
              Dismiss
            </button>
          </details>
        )}
        {status?.affinity.enabled === true && !status.affinity.real && (
          <p className="subtle">Against the fake unless AFFINITY_BASE_URL points at Affinity.</p>
        )}
      </CollapsibleCard>

      <CollapsibleCard id="dealroom" title="Dealroom" status={dealroomStatus}>
        <button
          className="button small"
          type="button"
          disabled={props.dealroom.isStarting || props.dealroom.status?.configured === false}
          onClick={props.dealroom.startMatch}
        >
          Match Dealroom
        </button>
        {status !== undefined && (
          <>
            <p className="subtle">
              {status.dealroom.callsToday}/{status.dealroom.ceilingPerDay} calls today
            </p>
            <p className="subtle">{runPhrase(status.dealroom)}</p>
            <p className="subtle">
              <Link href="/discover">{candidatesPhrase(status.dealroom.candidatesPending)}</Link>
            </p>
            <p className="subtle">
              <Link href="/rounds">{roundsPhrase(status.dealroom.roundsThisWeek)}</Link>
            </p>
          </>
        )}
      </CollapsibleCard>

      <StageConflicts />

      <PeopleChanges selectedFundIds={props.selectedFundIds} />

      <ReportedContacts needsReviewCount={status?.contacts.needsReviewCount} />

      <PossibleDuplicates />
    </aside>
  );
}

function pendingFundsPhrase(count: number): string {
  if (count === 0) return 'no funds have a pending proposal';
  return count === 1 ? '1 fund has a pending proposal' : `${count} funds have a pending proposal`;
}

function candidatesPhrase(count: number): string {
  return count === 1 ? '1 fund discovered' : `${count} funds discovered`;
}

function roundsPhrase(count: number): string {
  return count === 1 ? '1 round this week' : `${count} rounds this week`;
}
